<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import PermissionsMatrix from './PermissionsMatrix.vue';
import UserPermissionsMatrix from './UserPermissionsMatrix.vue';
import { useRbacStore } from '@/stores/rbac.js';
import { useToast } from 'primevue';

const props = defineProps({
  visible: Boolean,
  entityType: {
    type: String,
    required: true,
    validator: (value) => ['department', 'position', 'user'].includes(value)
  },
  entityId: {
    type: Number,
    default: null
  },
  entityName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible', 'saved', 'close']);

const rbacStore = useRbacStore();
const toast = useToast();

const permissions = ref([]);
const basePermissions = ref([]);
const extraPermissions = ref([]);
const revokedPermissions = ref([]);
const loading = ref(false);
const saving = ref(false);

const dialogTitle = computed(() => {
  const typeLabel = props.entityType === 'department' ? 'отдела' :
                    props.entityType === 'position' ? 'должности' : 'пользователя';
  return `Настройка прав ${typeLabel}: ${props.entityName}`;
});

watch(() => props.visible, async (newVal) => {
  if (newVal && props.entityId) {
    await loadPermissions();
  }
});

async function loadPermissions() {
  loading.value = true;
  try {
    const data = await rbacStore.loadPermissions(props.entityType, props.entityId);

    if (props.entityType === 'user') {
      // Для пользователя используем новый формат
      basePermissions.value = Array.isArray(data?.base_permissions) ? data.base_permissions : [];
      extraPermissions.value = Array.isArray(data?.extra_permissions) ? data.extra_permissions : [];
      revokedPermissions.value = Array.isArray(data?.revoked_permissions) ? data.revoked_permissions : [];
    } else {
      // Для отдела/должности используем старый формат
      permissions.value = Array.isArray(data?.permissions) ? data.permissions : [];
    }
  } catch (error) {
    console.error('Ошибка загрузки прав:', error);
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить права доступа',
      life: 3000,
    });
    permissions.value = [];
    basePermissions.value = [];
    extraPermissions.value = [];
    revokedPermissions.value = [];
  } finally {
    loading.value = false;
  }
}

async function handleSave() {
  saving.value = true;
  try {
    if (props.entityType === 'user') {
      // Для пользователя отправляем extra и revoked
      await rbacStore.updateUserPermissions(props.entityId, {
        extra_permissions: extraPermissions.value,
        revoked_permissions: revokedPermissions.value
      });
    } else {
      // Для отдела/должности отправляем обычный массив
      await rbacStore.updatePermissions(props.entityType, props.entityId, permissions.value);
    }
    emit('saved');
    emit('update:visible', false);
  } catch (error) {
    console.error('Ошибка сохранения прав:', error);
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось сохранить права доступа';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  } finally {
    saving.value = false;
  }
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
}

function handlePermissionsUpdate(newPermissions) {
  permissions.value = newPermissions;
}

function handleUserPermissionsUpdate(newExtra, newRevoked) {
  extraPermissions.value = newExtra;
  revokedPermissions.value = newRevoked;
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="dialogTitle"
    modal
    :style="{ width: '90vw', maxWidth: '1200px', height: '70vh', maxHeight: '800px' }"
    :closable="!saving"
  >
    <div v-if="loading" class="flex justify-center items-center py-8">
      <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
    </div>

    <!-- Для пользователя используем UserPermissionsMatrix -->
    <UserPermissionsMatrix
      v-else-if="entityType === 'user'"
      :base-permissions="basePermissions"
      :extra-permissions="extraPermissions"
      :revoked-permissions="revokedPermissions"
      @update:extra-permissions="(v) => handleUserPermissionsUpdate(v, revokedPermissions)"
      @update:revoked-permissions="(v) => handleUserPermissionsUpdate(extraPermissions, v)"
    />

    <!-- Для отдела/должности используем обычный PermissionsMatrix -->
    <PermissionsMatrix
      v-else
      :permissions="permissions"
      @update:permissions="handlePermissionsUpdate"
    />

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Отмена"
          severity="secondary"
          @click="handleClose"
          rounded
          size="small"
          :disabled="saving"
        />
        <Button
          label="Сохранить"
          severity="info"
          @click="handleSave"
          rounded
          size="small"
          :loading="saving"
          :disabled="loading"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
